import { Injectable } from '@angular/core';
import { AlertController, LoadingController, ModalController, ToastController } from '@ionic/angular';
import {
  TBctxWithMultipleSizeAndCount,
  TBulkEvent,
  TBulkCountEvent,
  TSingleCountEvent,
  TForwardBulkEvent,
  TForwardSingleEvent,
  TBctxItemWithoutSize,
  TBCTXKeys,
  TAppIdentifier,
  TBulkCountAppEventWithStatus,
  TTagType,
} from '@monorepo/types';
import { WipValidationErrorModalComponent } from '@monorepo/ui';
import { InputAppClientDBFacade } from '@monorepo/core-data';
import { ToBeRectifiedErrorModalComponent } from 'libs/ui/src/lib/input-app/md-point/to-be-rectified-error-modal/to-be-rectified-error-modal.component';
import { EventUtil } from './event-util';
import { ErrorModalComponent } from 'libs/ui/src/lib/input-app/md-point/error-modal/error-modal.component';
import produce from 'immer';
import { take } from 'rxjs/operators';
import { ValidationErrorModalComponent } from 'libs/ui/src/lib/input-app/bulk-count/validation-error-modal/validation-error-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { LOCAL_STORAGE_UTILS } from '../localStorage.utils';
import { HttpErrorResponse } from '@angular/common/http';
@Injectable({ providedIn: 'root' })
export class InputAppAPIHelperUtil {
  public syncEventsRetryCount: number = 3;
  alarm: HTMLAudioElement = new Audio('/assets/common/mm_notif_sound.wav');
  constructor(
    private modalCtrl: ModalController,
    private loadingController: LoadingController,
    private alertCtrl: AlertController,
    private inputAppClientDBFacade: InputAppClientDBFacade,
    private toastCtrl: ToastController,
    private translate: TranslateService,
  ) {}

  async presentLoading(message = 'Please wait') {
    const loading = await this.loadingController.create({
      message: message,
      backdropDismiss: false,
      keyboardClose: false,
    });
    await loading.present();
  }

  async dismissLoading() {
    await this.loadingController.dismiss();
  }

  async showToast(message: string, placeTop = false) {
    if (message) {
      const toast = await this.toastCtrl.create({
        message,
        duration: 3000,
        cssClass: 'success-tost',
        position: placeTop ? 'top' : 'bottom',
      });
      await toast.present();
    }
  }

  async showGenericToast(data: { message: string; duration: number; cssClass: string }, placeTopElseBottom: boolean) {
    const toast = await this.toastCtrl.create({
      message: data.message,
      duration: data.duration,
      cssClass: data.cssClass,
      position: placeTopElseBottom ? 'top' : 'bottom',
    });
    await toast.present();
  }

  async showFailureToast(message: string, placeTop = false) {
    if (message) {
      const toast = await this.toastCtrl.create({
        message,
        duration: 3000,
        cssClass: 'failure-toast',
        position: placeTop ? 'top' : 'bottom',
      });
      await toast.present();
    }
  }

  async showFalureToastWithDuration(message: string, duration: number, placeTop = false) {
    if (message) {
      const toast = await this.toastCtrl.create({
        message,
        duration: duration,
        cssClass: 'failure-toast',
        position: placeTop ? 'top' : 'bottom',
      });
      await toast.present();
    }
  }

  async showPopup(
    title: string,
    message: string,
    buttons = [
      {
        text: 'Ok',
      },
    ],
    cssClass: string | null = null,
  ) {
    const isAlarmEnabled = LOCAL_STORAGE_UTILS.retrieveData('alarmSignal');
    const alert = await this.alertCtrl.create({
      header: title,
      subHeader: message,
      buttons: buttons,
      ...(cssClass && {
        cssClass: cssClass,
      }),
    });

    if (isAlarmEnabled === 'enabled') {
      this.alarm.loop = true;
      this.alarm.play();
      alert.onDidDismiss().then(() => {
        console.log('alert dismissed');
        this.alarm.pause();
        this.alarm.currentTime = 0;
      });
    }
    alert.present();
  }

  async showLogoutFailurePopup(updateActualsOnly: boolean) {
    const alert = await this.alertCtrl.create({
      header: 'Warning',
      subHeader: "Can't sync all the pending events, Do you want to retry or logout without syncing pending events?",
      buttons: [
        this.syncEventsRetryCount > 0
          ? {
              text: 'Cancel',
              handler: () => {
                this.resetSyncEventsRetryCount();
              },
            }
          : {
              text: 'Logout',
              handler: () => {
                this.resetSyncEventsRetryCount();
                this.inputAppClientDBFacade.emitLogoutAppSubscription();
              },
            },
        {
          text: 'Retry',
          handler: () => {
            this.syncEventsRetryCount--;
            this.inputAppClientDBFacade.forceLogoutAfterSyncingAllPendingEventsWrapper(updateActualsOnly);
          },
        },
      ],
    });
    alert.present();
  }

  resetSyncEventsRetryCount() {
    this.syncEventsRetryCount = 3;
  }

  showWipValidationErrorNew(
    appIdentifier: TAppIdentifier | false,
    updateActualsOnly: boolean,
    bulkEvents: TBulkCountEvent[],
    bctxKeysToDisplayMap: Record<TBCTXKeys, string>,
    rfidScannerBulkMode: boolean,
  ) {
    this.inputAppClientDBFacade
      .getSelectedStationIdAndShiftId()
      .pipe(take(1))
      .subscribe(async ({ shiftId, stationId }) => {
        const failedEvents = bulkEvents.reduce((acc, curr) => {
          if (EventUtil.hasEventHappenedInStationDetails(curr, stationId, shiftId)) {
            curr.events.forEach((singleEvent) => {
              if (singleEvent.eventBackgroundMetadata.status === 'VALIDATION_ERROR') {
                acc.push(singleEvent);
              }
            });
          }
          return acc;
        }, [] as TSingleCountEvent[]);
        if (failedEvents.length > 0) {
          const modal = await this.modalCtrl.create({
            component: ToBeRectifiedErrorModalComponent,
            componentProps: {
              failedEvents,
              bctxKeysToDisplayMap,
              appIdentifier,
              rfidScannerBulkMode,
            },
            cssClass: 'm-modal__rectified-error-modal',
            backdropDismiss: false,
          });
          await modal.present();
          const isAlarmEnabled = LOCAL_STORAGE_UTILS.retrieveData('alarmSignal');
          if (isAlarmEnabled === 'enabled') {
            this.alarm.loop = true;
            this.alarm.play();
          }
          modal.onDidDismiss().then(() => {
            if (isAlarmEnabled === 'enabled') {
              this.alarm.pause();
              this.alarm.currentTime = 0;
            }
            if (appIdentifier === 'endline_qc_v2') {
              this.inputAppClientDBFacade.restoreAppStateForBackedUpBctx(
                updateActualsOnly,
                { wipOnly: true, actualsOnly: updateActualsOnly, chainWithLoadMasterdataList: false },
                false,
                false,
              );
            }
          });
        }
      });
  }

  async showFailedEvents(
    appIdentifier: TAppIdentifier | false,
    updateActualsOnly: boolean,
    eventList: TBulkEvent[],
    failedEventsIndexes: number[],
    bctxKeysToDisplayMap: Record<TBCTXKeys, string>,
    rfidScannerBulkMode: boolean,
  ) {
    if (failedEventsIndexes.length > 0) {
      this.showWipValidationErrorNew(
        appIdentifier,
        updateActualsOnly,
        eventList.filter(
          (event, index) =>
            failedEventsIndexes.some((fIndex) => fIndex === index) &&
            (event.type === 'FORWARD_BULK_EVENT' || event.type === 'UNDO_BULK_EVENT'),
        ) as TBulkCountEvent[],
        bctxKeysToDisplayMap,
        rfidScannerBulkMode,
      );
    }
  }

  async showWipValidationErrorOnline(singleEvents: TForwardSingleEvent[]) {
    const modal = await this.modalCtrl.create({
      component: WipValidationErrorModalComponent,
      componentProps: {
        errorTxt: 'input_app_api_helper.wipValidationErrorOnlineErrorText',
        sizeAndCountList: singleEvents.map((event) => {
          return {
            name: event.eventInfo.bctxDetails.size,
            count: event.eventInfo.bctxDetails.count,
          };
        }),
      },
      cssClass: 'm-modal__default-error-modal',
    });
    await modal.present();
    const isAlarmEnabled = LOCAL_STORAGE_UTILS.retrieveData('alarmSignal');
    if (isAlarmEnabled === 'enabled') {
      this.alarm.loop = true;
      this.alarm.play();
    }
    modal.onDidDismiss().then(() => {
      if (isAlarmEnabled === 'enabled') {
        this.alarm.pause();
        this.alarm.currentTime = 0;
      }
      this.inputAppClientDBFacade.restoreAppState(
        [
          {
            sizes: singleEvents.map((event) => event.eventInfo.bctxDetails.size),
            preSizeLevelBctx: singleEvents[0].eventInfo.bctxDetails.businessContextDetailsWithoutSize,
          },
        ],
        true,
      );
    });
  }

  async showWipValidationErrorForBulkCountApp(
    failedEvents: TBulkCountAppEventWithStatus[],
    bctxKeysToDisplayMap: Record<TBCTXKeys, string>,
  ) {
    const modal = await this.modalCtrl.create({
      component: ValidationErrorModalComponent,
      componentProps: {
        failedEvents: failedEvents,
        bctxKeysToDisplayMap,
      },
      cssClass: 'm-modal__default-error-modal',
    });
    await modal.present();
    const isAlarmEnabled = LOCAL_STORAGE_UTILS.retrieveData('alarmSignal');
    if (isAlarmEnabled === 'enabled') {
      this.alarm.loop = true;
      this.alarm.play();
    }
    modal.onDidDismiss().then(() => {
      if (isAlarmEnabled === 'enabled') {
        this.alarm.pause();
        this.alarm.currentTime = 0;
      }
      // TODO: write restore logic here
    });
  }

  async showFailedEventsOnline(bulkEvent: TForwardBulkEvent) {
    const filteredEvents = bulkEvent.events.filter(
      (event) => event.eventBackgroundMetadata.status === 'VALIDATION_ERROR',
    );
    if (filteredEvents.length > 0) {
      this.showWipValidationErrorOnline(filteredEvents);
    }
  }

  async showValidationErrorToBeRectifiedItems(
    bctxItemsWithCount: TBctxWithMultipleSizeAndCount[],
    bctxKeysToDisplayMap: Record<TBCTXKeys, string>,
    isRework: boolean,
  ) {
    const modal = await this.modalCtrl.create({
      component: ErrorModalComponent,
      componentProps: {
        errorTxt: `${this.translate.instant('input_app_api_helper.failedTo')} ${
          isRework
            ? this.translate.instant('input_app_api_helper.rectify')
            : this.translate.instant('input_app_api_helper.reject')
        } ${this.translate.instant('input_app_api_helper.toBeRectifiedErrorMessage')}`,
        bctxItemsWithCount,
        bctxKeysToDisplayMap,
      },
      cssClass: 'm-modal__rectified-error-modal',
      backdropDismiss: false,
    });
    await modal.present();
    const isAlarmEnabled = LOCAL_STORAGE_UTILS.retrieveData('alarmSignal');
    if (isAlarmEnabled === 'enabled') {
      this.alarm.loop = true;
      this.alarm.play();
    }
    modal.onDidDismiss().then(() => {
      if (isAlarmEnabled === 'enabled') {
        this.alarm.pause();
        this.alarm.currentTime = 0;
      }
      this.inputAppClientDBFacade.restoreAppState(
        bctxItemsWithCount.map((bctxItem) => {
          return {
            preSizeLevelBctx: bctxItem.preSizeLevelBctx,
            sizes: bctxItem.sizes.map((size) => size.name),
          };
        }),
      );
    });
  }

  #mergeEvents(list: TSingleCountEvent[]) {
    const mergedEventMap = new Map<
      string,
      { bctx: TBctxItemWithoutSize[]; sizeToCount: Record<string, { size: string; count: number }> }
    >();
    list.forEach((item) => {
      const eventKey = item.eventInfo.bctxDetails.bctxKey + '_' + item.actionType;
      const size = item.eventInfo.bctxDetails.size;
      const count = item.eventInfo.bctxDetails.count;
      const event = mergedEventMap.get(eventKey);
      if (event) {
        if (!event.sizeToCount[size]) {
          mergedEventMap.set(
            eventKey,
            produce(event, (draft) => {
              draft.sizeToCount[size] = {
                size: size,
                count: count,
              };
            }),
          );
        } else {
          mergedEventMap.set(
            eventKey,
            produce(event, (draft) => {
              draft.sizeToCount[size].count = draft.sizeToCount[size].count + count;
            }),
          );
        }
      } else
        mergedEventMap.set(eventKey, {
          bctx: item.eventInfo.bctxDetails.businessContextDetailsWithoutSize,
          sizeToCount: {
            [size]: {
              size: size,
              count: count,
            },
          },
        });
    });
    const mergedEvents: TBctxWithMultipleSizeAndCount[] = [];
    mergedEventMap.forEach((event) => {
      mergedEvents.push({
        preSizeLevelBctx: event.bctx,
        sizes: Object.values(event.sizeToCount).map(({ size, count }) => {
          return {
            name: size,
            count: count,
          };
        }),
      });
    });
    return mergedEvents;
  }

  async showFailedDefectiveResolutionEventsOnline(
    bulkEvent: TForwardBulkEvent,
    bctxKeysToDisplayMap: Record<TBCTXKeys, string>,
  ) {
    const filteredEvents = bulkEvent.events.filter(
      (event) => event.eventBackgroundMetadata.status === 'VALIDATION_ERROR',
    );
    const mergedEvents = this.#mergeEvents(filteredEvents);
    if (mergedEvents.length > 0) {
      this.showValidationErrorToBeRectifiedItems(
        mergedEvents,
        bctxKeysToDisplayMap,
        bulkEvent.events[0].actionType === 'REWORKED_FROM_DEFECTIVE',
      );
    }
  }

  async dimissModalsRecursevily(toBeClosedModals: number) {
    if (toBeClosedModals === 0) return;
    await this.modalCtrl.dismiss();
    this.dimissModalsRecursevily(toBeClosedModals - 1);
  }

  dismissModals(toBeClosedModals: number) {
    return new Promise<boolean>((res) => {
      this.dimissModalsRecursevily(toBeClosedModals)
        .then(() => {
          console.log('removed all modals');
          res(true);
        })
        .catch(() => res(false));
    });
  }

  getTagTypeFromTagId(tagId: string): TTagType | '' {
    const firstDigit = tagId.charAt(0);
    if (firstDigit === '1') {
      return TTagType.MASTER_BUNDLE_TAG;
    } else if (firstDigit === '3') {
      return TTagType.COMPONENT_BUNDLE_TAG;
    } else if (firstDigit === '2') {
      return TTagType.PER_PIECE_TAG;
    }
    return '';
  }
  handleHttpErrors(error: HttpErrorResponse) {
    if (error.status === 500) {
      this.showFailureToast(
        this.translate.instant('cutbank_errors.technicalIssueTitle'),
        this.translate.instant('cutbank_errors.technicalIssueMessage'),
      );
    } else {
      this.showFailureToast(
        this.translate.instant('cutbank_errors.networkErrorMessage'),
        this.translate.instant('cutbank_errors.networkErrorTitle'),
      );
    }
  }
}
